import React from 'react';
import { FileText, Download, Eye, Archive, Trash2, Calendar, User, Tag } from 'lucide-react';
import Button from '../../../components/ui/Button';

const DocumentCard = ({
  document,
  viewMode = 'grid',
  isSelected = false,
  onToggleSelect,
  onPreview,
  onDownload,
  onArchive,
  onDelete
}) => {
  const getCategoryColor = (category) => {
    const colors = {
      payslip: 'bg-blue-100 text-blue-700',
      employment_letter: 'bg-purple-100 text-purple-700',
      contract: 'bg-emerald-100 text-emerald-700',
      compliance: 'bg-amber-100 text-amber-700',
      personal: 'bg-gray-100 text-gray-700'
    };
    return colors?.[category] || 'bg-gray-100 text-gray-700';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024)?.toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (viewMode === 'list') {
    return (
      <div
        className={`bg-white rounded-lg shadow-sm border ${
          isSelected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-gray-200'
        } p-4 hover:shadow-md transition-shadow`}
      >
        <div className="flex items-center gap-4">
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
          />

          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-gray-600" />
            </div>
          </div>

          {/* Document Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {document?.documentName}
            </h3>
            <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {document?.employeeName}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(document?.documentDate)}
              </span>
              <span>{formatFileSize(document?.fileSizeBytes)}</span>
            </div>
          </div>

          {/* Category Badge */}
          <div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(document?.category)}`}>
              {document?.category?.replace('_', ' ')?.toUpperCase()}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              onClick={onPreview}
              iconName="Eye"
              variant="ghost"
              size="sm"
              title="Preview"
            />
            <Button
              onClick={onDownload}
              iconName="Download"
              variant="ghost"
              size="sm"
              title="Download"
            />
            <Button
              onClick={onArchive}
              iconName="Archive"
              variant="ghost"
              size="sm"
              title="Archive"
            />
            <Button
              onClick={onDelete}
              iconName="Trash2"
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
              title="Delete"
            />
          </div>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border ${
        isSelected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-gray-200'
      } overflow-hidden hover:shadow-md transition-shadow`}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start justify-between mb-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 mt-1"
          />
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(document?.category)}`}>
            {document?.category?.replace('_', ' ')?.toUpperCase()}
          </span>
        </div>

        <div className="flex items-center justify-center py-6">
          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
            <FileText className="w-8 h-8 text-gray-600" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">
          {document?.documentName}
        </h3>

        <div className="space-y-2 text-xs text-gray-600 mb-4">
          <div className="flex items-center gap-2">
            <User className="w-3 h-3" />
            <span className="truncate">{document?.employeeName}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-3 h-3" />
            <span>{formatDate(document?.documentDate)}</span>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="w-3 h-3" />
            <span>{formatFileSize(document?.fileSizeBytes)}</span>
          </div>
        </div>

        {document?.tags?.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap mb-4">
            {document?.tags?.slice(0, 3)?.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
              >
                <Tag className="w-3 h-3" />
                {tag}
              </span>
            ))}
            {document?.tags?.length > 3 && (
              <span className="text-xs text-gray-500">+{document?.tags?.length - 3}</span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={onPreview}
            iconName="Eye"
            variant="outline"
            size="sm"
            fullWidth
          >
            Preview
          </Button>
          <Button
            onClick={onDownload}
            iconName="Download"
            variant="outline"
            size="sm"
            fullWidth
          >
            Download
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-2">
          <Button
            onClick={onArchive}
            iconName="Archive"
            variant="ghost"
            size="sm"
            fullWidth
          >
            Archive
          </Button>
          <Button
            onClick={onDelete}
            iconName="Trash2"
            variant="ghost"
            size="sm"
            fullWidth
            className="text-red-600 hover:text-red-700"
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentCard;